// frontend/src/context/FamilyContext.js
import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from './AuthContext';
import api from '../services/api';

// Create context
export const FamilyContext = createContext();

export const FamilyProvider = ({ children }) => {
    const { token, user } = useContext(AuthContext);
    const [families, setFamilies] = useState([]);
    const [activeFamily, setActiveFamily] = useState(null);
    const [familyMembers, setFamilyMembers] = useState([]);
    const [messages, setMessages] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [familyAccounts, setFamilyAccounts] = useState([]);
    // Safe invitation views from GET /api/family/:id (no raw tokens; the
    // link is present only while an invitation is pending).
    const [pendingInvitations, setPendingInvitations] = useState([]);
    const [userRole, setUserRole] = useState(null);
    const [permissions, setPermissions] = useState({
      canModify: true,
      canDelete: true,
      canManageMembers: false,
      canConnectAccounts: true,
      canDisconnectAccounts: true
    });
  
    // Fetch user's family groups
    const fetchFamilies = useCallback(async () => {
      if (!token) return;
      
      try {
        setLoading(true);
        setError(null);
        
        const response = await api.get('/api/family', {
          headers: { 'x-auth-token': token }
        });
        
        setFamilies(response.data);
        
        // Set active family if not already set
        if (response.data.length > 0 && !activeFamily) {
          // Try to find user's active family
          if (user && user.activeFamilyGroup) {
            const active = response.data.find(f => f._id === user.activeFamilyGroup);
            if (active) {
              setActiveFamily(active);
            } else {
              setActiveFamily(response.data[0]);
            }
          } else {
            setActiveFamily(response.data[0]);
          }
        }
      } catch (err) {
        console.error('Error fetching family groups:', err);
        setError('Failed to load family groups');
      } finally {
        setLoading(false);
      }
    }, [token, activeFamily, user]);
  
    // Fetch active family members
    const fetchFamilyMembers = useCallback(async () => {
      if (!token || !activeFamily) return;
      
      try {
        setLoading(true);
        
        const response = await api.get(`/api/family/${activeFamily._id}`, {
          headers: { 'x-auth-token': token }
        });
        
        setFamilyMembers(response.data.members);
        setPendingInvitations(Array.isArray(response.data.invitations) ? response.data.invitations : []);
      } catch (err) {
        console.error('Error fetching family members:', err);
      } finally {
        setLoading(false);
      }
    }, [token, activeFamily]);
  
    // Fetch family messages
    const fetchMessages = useCallback(async (limit = 50, skip = 0, category = null) => {
      if (!token || !activeFamily) return [];
      
      try {
        setLoading(true);
        
        let url = `/api/family/${activeFamily._id}/messages?limit=${limit}&skip=${skip}`;
        if (category) {
          url += `&category=${category}`;
        }
        
        const response = await api.get(url, {
          headers: { 'x-auth-token': token }
        });
        
        if (skip === 0) {
          // Replace messages with new data if this is the first page
          setMessages(response.data.messages);
        } else {
          // Append messages if loading more
          setMessages(prevMessages => [...prevMessages, ...response.data.messages]);
        }
        
        return response.data;
      } catch (err) {
        console.error('Error fetching family messages:', err);
        return { messages: [], hasMore: false };
      } finally {
        setLoading(false);
      }
    }, [token, activeFamily]);
  
    // Send a message
    const sendMessage = useCallback(async (content, isImportant = false, category = 'general') => {
      if (!token || !activeFamily) return null;
      
      try {
        setLoading(true);
        
        const response = await api.post(`/api/family/${activeFamily._id}/messages`, {
          content,
          isImportant,
          category
        }, {
          headers: { 'x-auth-token': token }
        });
        
        // Add new message to the top of the list
        setMessages(prevMessages => [response.data.familyMessage, ...prevMessages]);
        
        return response.data.familyMessage;
      } catch (err) {
        console.error('Error sending message:', err);
        return null;
      } finally {
        setLoading(false);
      }
    }, [token, activeFamily]);
  
    // Mark a message as read
    const markMessageAsRead = useCallback(async (messageId) => {
      if (!token) return false;
      
      try {
        await api.post(`/api/family/messages/${messageId}/read`, {}, {
          headers: { 'x-auth-token': token }
        });
        
        // Update unread count
        fetchUnreadCount();
        
        return true;
      } catch (err) {
        console.error('Error marking message as read:', err);
        return false;
      }
    }, [token]);
  
    // Delete a message
    const deleteMessage = useCallback(async (messageId) => {
      if (!token) return false;
      
      try {
        await api.delete(`/api/family/messages/${messageId}`, {
          headers: { 'x-auth-token': token }
        });
        
        // Remove message from state
        setMessages(prevMessages => prevMessages.filter(msg => msg._id !== messageId));
        
        return true;
      } catch (err) {
        console.error('Error deleting message:', err);
        return false;
      }
    }, [token]);
  
    // Get unread message count
    const fetchUnreadCount = useCallback(async () => {
      if (!token) return;
      
      try {
        const response = await api.get('/api/family/messages/unread', {
          headers: { 'x-auth-token': token }
        });
        
        setUnreadCount(response.data.total);
      } catch (err) {
        console.error('Error fetching unread count:', err);
      }
    }, [token]);
  
    // Create a new family group
    const createFamily = useCallback(async (name) => {
      if (!token) return null;
      
      try {
        setLoading(true);
        
        const response = await api.post('/api/family', { name }, {
          headers: { 'x-auth-token': token }
        });
        
        // Add new family to list and set as active
        const newFamily = response.data.familyGroup;
        setFamilies(prevFamilies => [...prevFamilies, newFamily]);
        setActiveFamily(newFamily);
        
        return newFamily;
      } catch (err) {
        console.error('Error creating family group:', err);
        setError('Failed to create family group');
        return null;
      } finally {
        setLoading(false);
      }
    }, [token]);
  
    // Invite someone to the family
    const inviteToFamily = useCallback(async (email, role = 'modify') => {
      if (!token || !activeFamily) return { success: false, error: 'Not authenticated' };

      try {
        setLoading(true);
        setError(null);

        const response = await api.post(`/api/family/${activeFamily._id}/invite`, { email, role }, {
          headers: { 'x-auth-token': token }
        });

        // Return the full response including email status
        return {
          success: true,
          invitation: response.data.invitation,
          message: response.data.message,
          emailSent: response.data.emailSent,
          isResend: response.data.isResend,
          emailError: response.data.emailError
        };
      } catch (err) {
        console.error('Error inviting to family:', err);
        const errorMsg = err.response?.data?.message || 'Failed to send invitation';
        setError(errorMsg);
        return { success: false, error: errorMsg };
      } finally {
        setLoading(false);
      }
    }, [token, activeFamily]);
  
    // Resend a pending invitation (renews it if it had expired)
    const resendInvitation = useCallback(async (invitationId) => {
      if (!token || !activeFamily) return { success: false, error: 'Not authenticated' };
      try {
        const response = await api.post(`/api/family/${activeFamily._id}/invitations/${invitationId}/resend`, {}, {
          headers: { 'x-auth-token': token }
        });
        await fetchFamilyMembers();
        return { success: true, ...response.data };
      } catch (err) {
        const data = err.response?.data || {};
        return { success: false, code: data.code, error: data.message || 'Could not resend the invitation' };
      }
    }, [token, activeFamily, fetchFamilyMembers]);

    // Withdraw a pending invitation
    const revokeInvitation = useCallback(async (invitationId) => {
      if (!token || !activeFamily) return { success: false, error: 'Not authenticated' };
      try {
        await api.delete(`/api/family/${activeFamily._id}/invitations/${invitationId}`, {
          headers: { 'x-auth-token': token }
        });
        await fetchFamilyMembers();
        return { success: true };
      } catch (err) {
        const data = err.response?.data || {};
        return { success: false, code: data.code, error: data.message || 'Could not withdraw the invitation' };
      }
    }, [token, activeFamily, fetchFamilyMembers]);

    // Leave the active household: instant and unilateral.
    const leaveFamily = useCallback(async () => {
      if (!token || !activeFamily) return { success: false, error: 'Not authenticated' };
      try {
        setLoading(true);
        const response = await api.post(`/api/family/${activeFamily._id}/leave`, {}, {
          headers: { 'x-auth-token': token }
        });
        setActiveFamily(null);
        setFamilies([]);
        setFamilyMembers([]);
        setPendingInvitations([]);
        setFamilyAccounts([]);
        setMessages([]);
        setUserRole(null);
        return { success: true, householdDeleted: Boolean(response.data?.householdDeleted) };
      } catch (err) {
        const data = err.response?.data || {};
        return { success: false, code: data.code, error: data.message || 'Could not leave the household' };
      } finally {
        setLoading(false);
      }
    }, [token, activeFamily]);

    // Accept an invitation
    const acceptInvitation = useCallback(async (invitationToken) => {
      if (!invitationToken || !token) return { success: false };

      try {
        setLoading(true);

        const response = await api.post(`/api/family/invitation/${invitationToken}/accept`, {}, {
          headers: { 'x-auth-token': token }
        });

        // Refresh families
        fetchFamilies();

        return {
          success: true,
          familyGroup: response.data.familyGroup,
          sharedCalendars: response.data.familyGroup?.sharedCalendars || false
        };
      } catch (err) {
        console.error('Error accepting invitation:', err);
        setError(err.response?.data?.message || 'Failed to accept invitation');
        return { success: false, error: err.response?.data?.message };
      } finally {
        setLoading(false);
      }
    }, [token, fetchFamilies]);
  
    // Fetch family accounts
    const fetchFamilyAccounts = useCallback(async () => {
      if (!token || !activeFamily) return [];

      try {
        setLoading(true);

        const response = await api.get(`/api/family/${activeFamily._id}/accounts`, {
          headers: { 'x-auth-token': token }
        });

        setFamilyAccounts(response.data.accounts || []);
        return response.data;
      } catch (err) {
        console.error('Error fetching family accounts:', err);
        return { accounts: [] };
      } finally {
        setLoading(false);
      }
    }, [token, activeFamily]);

    // Fetch user's role and permissions in active family
    const fetchUserRole = useCallback(async () => {
      if (!token || !activeFamily) {
        // Not in a family - full permissions
        setUserRole(null);
        setPermissions({
          canModify: true,
          canDelete: true,
          canManageMembers: false,
          canConnectAccounts: true,
          canDisconnectAccounts: true
        });
        return null;
      }

      try {
        const response = await api.get(`/api/family/${activeFamily._id}/role`, {
          headers: { 'x-auth-token': token }
        });

        setUserRole(response.data.role);
        setPermissions(response.data.permissions);
        return response.data;
      } catch (err) {
        console.error('Error fetching user role:', err);
        // Default to full permissions on error
        setUserRole(null);
        setPermissions({
          canModify: true,
          canDelete: true,
          canManageMembers: false,
          canConnectAccounts: true,
          canDisconnectAccounts: true
        });
        return null;
      }
    }, [token, activeFamily]);

    // Change a member's role (admin only)
    const changeMemberRole = useCallback(async (memberId, newRole) => {
      if (!token || !activeFamily) return { success: false, error: 'Not authenticated' };

      try {
        setLoading(true);

        const response = await api.put(`/api/family/${activeFamily._id}/members/${memberId}/role`,
          { role: newRole },
          { headers: { 'x-auth-token': token } }
        );

        // Refresh family members to show updated roles
        await fetchFamilyMembers();

        return { success: true, message: response.data.message };
      } catch (err) {
        console.error('Error changing member role:', err);
        const errorMsg = err.response?.data?.message || 'Failed to change member role';
        return { success: false, error: errorMsg };
      } finally {
        setLoading(false);
      }
    }, [token, activeFamily, fetchFamilyMembers]);

    // Assign account to family member or mark as shared
    const assignAccountToFamily = useCallback(async (accountId, assignedTo, assignedToName, isShared) => {
      if (!token || !activeFamily) return false;

      try {
        setLoading(true);

        await api.put(`/api/family/${activeFamily._id}/accounts/${accountId}/assign`, {
          assignedTo,
          assignedToName,
          isShared
        }, {
          headers: { 'x-auth-token': token }
        });

        // Refresh family accounts
        await fetchFamilyAccounts();

        return true;
      } catch (err) {
        console.error('Error assigning account:', err);
        setError('Failed to assign account');
        return false;
      } finally {
        setLoading(false);
      }
    }, [token, activeFamily, fetchFamilyAccounts]);

    // Remove account family assignment
    const removeAccountAssignment = useCallback(async (accountId) => {
      if (!token || !activeFamily) return false;

      try {
        setLoading(true);

        await api.delete(`/api/family/${activeFamily._id}/accounts/${accountId}/assign`, {
          headers: { 'x-auth-token': token }
        });

        // Refresh family accounts
        await fetchFamilyAccounts();

        return true;
      } catch (err) {
        console.error('Error removing account assignment:', err);
        setError('Failed to remove account assignment');
        return false;
      } finally {
        setLoading(false);
      }
    }, [token, activeFamily, fetchFamilyAccounts]);

    // Switch active family
    const switchFamily = useCallback(async (familyId) => {
      if (!token) return false;
      
      try {
        setLoading(true);
        
        // Find family in the list
        const family = families.find(f => f._id === familyId);
        if (!family) {
          throw new Error('Family not found');
        }
        
        // Update on server
        await api.post(`/api/family/${familyId}/active`, {}, {
          headers: { 'x-auth-token': token }
        });
        
        // Update local state
        setActiveFamily(family);
        
        // Reset message state
        setMessages([]);
        
        // Fetch members for the new active family
        fetchFamilyMembers();
        
        return true;
      } catch (err) {
        console.error('Error switching family:', err);
        return false;
      } finally {
        setLoading(false);
      }
    }, [token, families, fetchFamilyMembers]);
  
    // Reset state when token is cleared (logout or invalid token)
    useEffect(() => {
      if (!token) {
        setFamilies([]);
        setActiveFamily(null);
        setFamilyMembers([]);
        setPendingInvitations([]);
        setMessages([]);
        setUnreadCount(0);
        setFamilyAccounts([]);
        setUserRole(null);
        setError(null);
      }
    }, [token]);

    // Load initial data
    useEffect(() => {
      if (token) {
        fetchFamilies();
        fetchUnreadCount();
      }
    }, [token, fetchFamilies, fetchUnreadCount]);
  
    // Load family members and user role when active family changes
    useEffect(() => {
      if (activeFamily) {
        fetchFamilyMembers();
        fetchMessages();
        fetchUserRole();
      }
    }, [activeFamily, fetchFamilyMembers, fetchMessages, fetchUserRole]);
  
    // Set up polling for new messages and unread count
    useEffect(() => {
      if (!token || !activeFamily) return;
      
      const intervalId = setInterval(() => {
        fetchUnreadCount();
        
        // Only fetch new messages if we're not loading
        if (!loading) {
          fetchMessages(10, 0);
        }
      }, 30000); // Poll every 30 seconds
      
      return () => clearInterval(intervalId);
    }, [token, activeFamily, fetchUnreadCount, fetchMessages, loading]);
  
    return (
      <FamilyContext.Provider
        value={{
          families,
          activeFamily,
          familyMembers,
          messages,
          unreadCount,
          familyAccounts,
          pendingInvitations,
          userRole,
          permissions,
          loading,
          error,
          fetchFamilies,
          fetchFamilyMembers,
          fetchMessages,
          fetchFamilyAccounts,
          fetchUserRole,
          sendMessage,
          markMessageAsRead,
          deleteMessage,
          resendInvitation,
          revokeInvitation,
          leaveFamily,
          createFamily,
          inviteToFamily,
          acceptInvitation,
          switchFamily,
          assignAccountToFamily,
          removeAccountAssignment,
          changeMemberRole
        }}
      >
        {children}
      </FamilyContext.Provider>
    );
  };
  
  // Custom hook to use the family context
  export const useFamilyContext = () => {
    const context = useContext(FamilyContext);
    
    if (!context) {
      throw new Error('useFamilyContext must be used within a FamilyProvider');
    }
    
    return context;
  };